import {
  Button,
  Card,
  Col,
  Divider,
  ListButton,
  Row,
  Show,
  Table,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { useNavigation, useOne } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { ETransferStatus } from 'enums';
import { ITransferStock, ITransferStockProduct } from 'interfaces';
import { formatCurrency } from 'utilities/shared';
import { getTransferStatusStyle } from 'utilities/transfer-status';

const { Title, Text } = Typography;

export const TransferStockShow = () => {
  const { id } = useParams();
  const { push } = useNavigation();
  const { data, isLoading } = useOne<ITransferStock>({
    resource: 'transferStockCopy',
    id: id || '',
    metaData: {
      fields: [
        'id',
        'transferDate',
        'totalStockValue',
        'transferStatus',
        'rejectionReason',
        'approvedAt',
        {
          fromWarehouse: ['name'],
        },
        {
          toWarehouse: ['name'],
        },
        {
          createdBy: ['name'],
        },
        {
          approvedBy: ['name'],
        },
        {
          transferStockProductsCopy: [
            'id',
            {
              productVariant: ['sku', 'name'],
            },
            'quantity',
            'unitPrice',
            'stockValue',
          ],
        },
      ],
    },
  });
  const record = data?.data;

  const status = record?.transferStatus ?? '';
  const isPendingOrRejected =
    status === ETransferStatus.PENDING_APPROVAL || status === ETransferStatus.REJECTED;

  const { textColor, backgroundColor } = getTransferStatusStyle(status);

  const pageHeaders = (
    <>
      {!isPendingOrRejected && (
        <Button
          onClick={() => {
            const endpoint = `show/${record?.id}/delivery-slip/create`;
            push(endpoint);
          }}
        >
          Delivery Slip
        </Button>
      )}
      <ListButton />
    </>
  );

  return (
    <>
      <Show isLoading={isLoading} headerProps={{ extra: pageHeaders }}>
        <Row gutter={[48, 32]} wrap>
          <Col xs={24}>
            <Card title="Information">
              {status && (
                <>
                  <Title level={5}>Status</Title>
                  <Tag
                    style={{
                      color: textColor,
                      backgroundColor,
                      height: '24px',
                      padding: '0px 8px',
                      borderRadius: '8px',
                      border: 'none',
                      fontFamily: 'Nunito Sans',
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {status}
                  </Tag>
                </>
              )}
              <Title level={5}>From Warehouse</Title>
              <Text>{record?.fromWarehouse?.name}</Text>
              <Title level={5}>To Warehouse</Title>
              <Text>{record?.toWarehouse?.name}</Text>
              <Title level={5}>Total Stock Value</Title>
              <Text>{formatCurrency(record?.totalStockValue || 0)}</Text>
              <Title level={5}>Date</Title>
              <Text>
                {record?.transferDate ? new Date(record.transferDate).toLocaleString() : ''}
              </Text>
              <Title level={5}>Created By</Title>
              <Text>{record?.createdBy?.name}</Text>
              {record?.approvedBy && (
                <>
                  <Title level={5}>Reviewed By</Title>
                  <Text>{record.approvedBy.name}</Text>
                </>
              )}
              {record?.approvedAt && (
                <>
                  <Title level={5}>Reviewed At</Title>
                  <Text>{new Date(record.approvedAt).toLocaleString()}</Text>
                </>
              )}
              {status === ETransferStatus.REJECTED && record?.rejectionReason && (
                <>
                  <Title level={5}>Rejection Reason</Title>
                  <Text type="danger">{record.rejectionReason}</Text>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Show>

      <Divider />
      <Row wrap={false}>
        <Col flex="auto">
          <Table
            dataSource={record?.transferStockProductsCopy ?? []}
            rowKey="id"
            scroll={{ x: true }}
          >
            <Table.Column dataIndex={['productVariant', 'sku']} title="SKU" />
            <Table.Column dataIndex={['productVariant', 'name']} title="Name" />
            <Table.Column dataIndex="unitPrice" title="Unit Price" />
            <Table.Column dataIndex="quantity" title="Quantity" />
            <Table.Column<ITransferStockProduct>
              dataIndex="stockValue"
              title="Stock Value"
              render={(_, record) => formatCurrency(record.stockValue)}
            />
          </Table>
        </Col>
      </Row>
    </>
  );
};
